/**
 * Login Page
 *
 * Shows "Sign in with Microsoft" button.
 * Redirects to Azure AD OAuth flow.
 */

import { useSearchParams } from 'react-router-dom';
import { login } from '@/lib/auth.js';
import { returnPathFor } from '@/lib/return-path.js';

const ERROR_MESSAGES: Record<string, string> = {
  auth_failed: 'Authentication failed. Please try again.',
  domain_not_allowed: 'Only @weblisters.com accounts are allowed.',
  no_code: 'Authentication was cancelled.',
};

export function LoginPage() {
  const [searchParams] = useSearchParams();
  const errorCode = searchParams.get('error');
  const errorMessage = errorCode ? ERROR_MESSAGES[errorCode] || 'An error occurred.' : null;

  function handleLogin() {
    // Back to the page that was opened, not the start page (LH-1508). Read the browser's
    // address at the click: filters update it directly, so the router's copy can be stale.
    login(returnPathFor(window.location.pathname, window.location.search, window.location.hash));
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="w-full max-w-sm rounded-lg bg-white p-8 shadow-md">
        <div className="mb-6 text-center">
          <span className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600 text-white" aria-hidden="true">
            <svg viewBox="0 0 16 16" className="h-5 w-5" fill="currentColor">
              <path d="M8 1.5l6.5 6.5L8 14.5 1.5 8zm0 3.2L4.7 8 8 11.3 11.3 8z" />
            </svg>
          </span>
          <h1 className="text-2xl font-bold text-gray-900">Jira Portal</h1>
          <p className="mt-1 text-sm text-gray-500">WebListers internal access</p>
        </div>

        {errorMessage && (
          <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-700">
            {errorMessage}
          </div>
        )}

        <button
          onClick={handleLogin}
          className="flex w-full items-center justify-center gap-2 rounded-md bg-[#2F2F2F] px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#1A1A1A]"
        >
          <svg className="h-5 w-5" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
            <rect x="1" y="1" width="9" height="9" fill="#F25022" />
            <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
            <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
            <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
          </svg>
          Sign in with Microsoft
        </button>

        <p className="mt-4 text-center text-xs text-gray-400">
          Use your @weblisters.com account
        </p>
      </div>
    </div>
  );
}
